import React, { useEffect, useState } from "react";
import { Box, Typography, Stack, Tooltip, CircularProgress } from "@mui/material";
import { CustomRadio, CustomTextField } from "../../../../../shared/components/inputfields/form-elements";
import { NavLink, useParams } from "react-router-dom";
import useNotification from "../../../../../shared/hooks/useNotification";
import { useApi } from "../../../../../shared/hooks/useApi";
import { TCAppConfig } from "../../../../../shared/app-configs/tc-app-configs";
import { LoaderComponent } from "../../../../../shared/components/loaders/page-loader";
import { Utills } from "../../../../../shared/app-configs/utilit";
import { InnerButton } from "../../../../../shared/components/buttons/floating-button-container";
import { useTransactionCertificateStore } from "../../../../../store/transaction-certificates.store";
import ViewerModalPopup from "../../../../../shared/components/modals/viewerModal";
import PdfViewer from "../../../../../shared/components/preview/pdf-viewer";
import { downloadDocx, DownloadDocxOptions } from "../../../../../shared/components/document/docx-downloader";
import { declarationByCertificateBodyService } from "../../tc-service/declaration-by-certificate-body.service";
import { IDeclarationSubmitReqDto } from "../../tc-models/tc-req-dto/declaration-by-certificate-body.req_dto";
import { ErrorHandler } from "../../../../../shared/utils/error-handler.utils";


const DeclarationByCertificateBody: React.FC<{
  onNextTab: any;
  completionMap: Record<string, boolean>;
}> = ({ onNextTab, completionMap }) => {
  const [outsourced, setOutsourced] = useState<string>("N");
  const [additionalText, setAdditionalText] = useState<string>("");
  const [outsourcedError, setOutsourcedError] = useState("");
  const [showDownload, setShowDownload] = useState(false);
  const [getTcStandardDetails, setTcStandardDetails] = useState<any[]>([]);
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const { notify } = useNotification();
  const { TE_ID, formtype } = useParams<string>();
  const hasCreateTc: boolean = formtype === "create-tc";
  const hasSubmitTc: boolean = formtype === "submited-tc";
  const hasRejectedTc: boolean = formtype === "rejected-tc";

  const { executePost, executeGet, loading: pageLoading } = useApi();
  const { cbLicensingCode } = useTransactionCertificateStore();

  // Modal handlers
  const togglePreviewDrawer = () => setPreviewOpen((prev) => !prev);

  // Fetch TC Standard & Existing Declaration
  useEffect(() => {
    if (!TE_ID) return;

    const fetchData = async () => {
      try {
        try {
          const response: any = await executeGet(TCAppConfig.GET_TC_BASE_DETAILS_V4, {
            tcId: TE_ID,
          });
          if (response?.data) {
            setTcStandardDetails(response?.data?.tcStandard);
          }
        } catch (e) {
          console.warn('Failed to load TC standards:', e);
        }

        // Fetch existing declaration values from declaration endpoint
        try {
          const declResp: any = await executeGet(TCAppConfig.GET_DECLARATION_SUBMIT_DETAILS_V4, { tcId: TE_ID });
          const respData = declResp?.data || declResp || {};

          const sellerDecl =
            respData?.tcSellerProductDeclarations ??
            "";

          const outsourcedVal =
            respData?.tcOutsourcedToSubcontractor ??
            "";

          // Transform API response to match radio button values
          let normalizedOutsourced = "";
          if (outsourcedVal) {
            const upperVal = String(outsourcedVal).toUpperCase();
            if (upperVal === "Y" || upperVal === "YES") {
              normalizedOutsourced = "Y";
            } else if (upperVal === "N" || upperVal === "NO") {
              normalizedOutsourced = "N";
            } else {
              normalizedOutsourced = String(outsourcedVal);
            }
          }

          if (sellerDecl || outsourcedVal) {
            setAdditionalText(String(sellerDecl || ""));
            setOutsourced(normalizedOutsourced);
            setShowDownload(false);
            setIsSubmitted(false);
          }
        } catch (e) {
          console.log('Failed to load declaration details:', e);
        }
      } catch (error) {
        notify("Unable to fetch TC or Declaration details", "error");
      }
    };

    fetchData();
  }, [TE_ID]);
  


const handleSubmit = async () => {
  // Validation: outsourced is required
  if (!outsourced || outsourced.trim() === "") {
    setOutsourcedError("Outsourced to Subcontractor is required");
    notify("Please fill in all mandatory fields before saving", "error");
    return;
  }
  setOutsourcedError("");

  try {
    const payload: IDeclarationSubmitReqDto = {
      tcId: TE_ID ?? "",
      tcSellerProductDeclarations: additionalText || "",
      tcOutsourcedToSubcontractor: outsourced || "",
    };

    const result = await executePost(TCAppConfig.POST_DECLARATION_SUBMIT_DETAILS_V4, payload);

    if (result?.status === 200) {
      notify("Declaration saved successfully", "success");
      setShowDownload(false);
      setIsSubmitted(false);
      await onNextTab();
    } else {
      const errorMessage = ErrorHandler.extractErrorMessage(result, "Unable to save declaration");
      notify(errorMessage, "error");
    }

  } catch (error: any) {
    console.error("handleSubmit error:", error);
    const errorMessage = ErrorHandler.extractErrorMessage(error, "Error submitting declaration");
    notify(errorMessage, "error");
  }
};
  
  // Submit TC
  const handleTCSubmit = async () => {
    if (!TE_ID) {
      notify("TC ID is required", "error");
      return;
    }

    try {
      // Call validation API first
      const validationResult = await executeGet(TCAppConfig.GET_TC_COMPONENT_NET_SHIPPING_WEIGHT_V4, {
        tcId: TE_ID,
      });

      // If validation request itself fails, stop here (useApi shows errors)
      if (!validationResult || validationResult.status !== 200) {
        const errorMessage = ErrorHandler.extractErrorMessage(
          validationResult,
          "Unable to validate TC before submit"
        );
        notify(errorMessage, "error");
        return;
      }

      // Check validation payload for any invalid products
      const rawValidation: any = validationResult?.data ?? validationResult ?? {};
      let validationData: any = rawValidation;

      // Support stringified JSON responses
      if (typeof validationData === "string") {
        try {
          validationData = JSON.parse(validationData);
        } catch (e) {
          console.warn("Failed to parse validation response string", e);
        }
      }

      // Also handle nested data/response shapes
      const products: any[] =
        (Array.isArray(validationData?.products) && validationData.products) ||
        (Array.isArray(validationData?.data?.products) && validationData.data.products) ||
        (Array.isArray(validationData?.response?.products) && validationData.response.products) ||
        [];

      const summary =
        validationData?.summary ||
        validationData?.data?.summary ||
        validationData?.response?.summary ||
        {};
      const invalidProducts = products
        .flatMap((p) => {
          const results: { article: string; msg: string }[] = [];
          const article =
            p?.productDetails?.tcProductArticleNo || p?.productId || "Unknown product";

          // Net weight validation
          const wv = p?.weightValidation;
          if (wv) {
            const remaining = Number(wv.remainingCapacity);
            const hasRemaining =
              !Number.isNaN(remaining) && remaining > 0; // treat remaining capacity as invalid
            const isInvalid = wv.isValid === false || hasRemaining;

            if (isInvalid) {
              const remainingText = Number.isNaN(remaining) ? "0" : remaining.toString();
              const msg = `Net weight mismatch for product article ${article}. ${remainingText} KG of Net weight is missing in components`;
              results.push({ article, msg });
            }
          }

          // Certified weight validation (API field: weightValidationCertified)
          const cv = (p as any)?.weightValidationCertified;
          if (cv) {
            const remaining = Number(cv.remainingCapacity);
            const hasRemaining =
              !Number.isNaN(remaining) && remaining > 0; // treat remaining capacity as invalid
            const isInvalid = cv.isValid === false || hasRemaining;

            if (isInvalid) {
              const remainingText = Number.isNaN(remaining) ? "0" : remaining.toString();
              const msg = `Certified weight mismatch for product article ${article}. ${remainingText} KG of certified weight is missing in components`;
              results.push({ article, msg });
            }
          }

          return results;
        })
        .filter(Boolean) as { article: string; msg: string }[];

      if ((summary?.invalidProducts ?? 0) > 0 || invalidProducts.length > 0) {
        const maxDisplay = 5;
        const displayList = invalidProducts.slice(0, maxDisplay);
        const extraCount = invalidProducts.length - displayList.length;

        // Send each product message separately
        if (displayList.length > 0) {
          displayList.forEach((p) => {
            notify(p.msg, "error");
          });
        } else {
          notify(
            "Net weight mismatch for one or more products. Net weight is missing in components.",
            "error"
          );
        }

        // If there are more than maxDisplay invalid products, show one summary message
        if (extraCount > 0) {
          notify(`and ${extraCount} more products with net weight mismatch`, "error");
        }

        // Do not proceed to TC submit when validation reports errors
        return;
      }

      // Validation passed, proceed with TC submit
      const result = await executePost(TCAppConfig.TC_SUBMIT_DATA_V4, { tcId: TE_ID });
      if (result?.status === 200) {
        notify("TC Submitted Successfully", "success");
        setIsSubmitted(true);
        setShowDownload(true);
      } else {
        const errorMessage = ErrorHandler.extractErrorMessage(result, "Unable to Submit");
        notify(errorMessage, "error");
      }
    } catch (error: any) {
      console.error("Failed to submit TC:", error);
      const errorMessage = ErrorHandler.extractErrorMessage(error, "Error submitting TC");
      notify(errorMessage, "error");
    }
  };

  // Generate JSON
  const handleGenerateJSON = async () => {
    try {
      const result = await executeGet(TCAppConfig.GET_DECLARATION_JSON_DETAILS, { tcCbLicensingCode: cbLicensingCode, tcId: TE_ID });
      if (result?.status === 200) {
        const blob = new Blob([JSON.stringify(result.data, null, 2)], { type: "application/json" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `declaration_${TE_ID}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        notify("JSON downloaded successfully", "success");
      } else notify("Failed to generate JSON", "error");
    } catch (error) {
      notify("Error generating JSON", "error");
    }
  };

  // Download Word DOCX
  const handleDownload = async () => {
    setIsDownloading(true);
    const options: DownloadDocxOptions = {
      apiUrl: TCAppConfig.GENERATE_WORD_TC_V4,
      params: { tcId: TE_ID ?? ""},
    };

    try {
      await downloadDocx(options);
    } catch (error) {
      console.error("DOCX download error:", error);
      notify("Failed to download DOCX", "error");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box p={4}>
      {pageLoading ? (
        <LoaderComponent />
      ) : (
        <>
          <Typography variant="body1" fontWeight="600">Declaration by Certification Body:</Typography>
          <Typography variant="body1">This is to certify that, based on the relevant documentation provided by the seller named in screen "Buyer & Seller Information":</Typography>
          <Typography variant="body1" component="div" sx={{ mt: 1, pl: 3 }}>
            <ul>
              <li>
              The raw material(s) for the products as further detailed/referred to screen "TC Products Information", "Certified Raw Material & Standard Information" and "Gross Shipping Weight", "Net Shipping Weight" and "Certified Weight" has/have been produced in accordance with the {getTcStandardDetails.map((item) => `(${item})`).join(", ")}.
              </li>
              <li>
              The products in "TC Products Information" have been processed in accordance with the {getTcStandardDetails?.length > 0 && (
              <>
                {getTcStandardDetails.map((item) => `(${item})`).join(", ")}.
              </>
            )}Conformity with the standard is audited and monitored systematically under the responsibility of the certification body.
              </li>
            </ul>
          </Typography>

          <Typography variant="body1" fontWeight="600" mt={2}>Declarations by Seller of Certified Products:</Typography>
          <Stack gap={2}>
            <Typography>The certified product(s) covered in this certificate have been outsourced to a subcontractor:</Typography>
            <Stack direction="row" alignItems="center">
              <Typography variant="body1" sx={{ mr: 2 }}>
                <Typography component="span" sx={{ color: "#a33b36" }}>*</Typography>
                TC Outsourced to Subcontractor
              </Typography>
        <CustomRadio
          readOnly={hasSubmitTc}
          name="outSource"
          value={outsourced}
          onChange={(e) => setOutsourced(e.target.value)}
          options={[
            { label: "Yes", value: "Y" },
            { label: "No", value: "N" },
          ]}
        />
            </Stack>
            {outsourcedError && <Typography color="error">{outsourcedError}</Typography>}
          </Stack>

          <CustomTextField
            label="Additional declaration text"
            variant="outlined"
            fullWidth
            multiline
            readOnly={hasSubmitTc}
            rows={3}
            value={additionalText}
            onChange={(e) => setAdditionalText(e.target.value)}
            sx={{ mb: 4 }}
          />

          <Box sx={{ display: "flex", justifyContent: "center", gap: "0.5rem" }}>
            <NavLink to="/v4/tc-dashboard">
              <InnerButton variant="contained">Home</InnerButton>
            </NavLink>
            
            {!(hasSubmitTc) && (
              <InnerButton variant="contained" onClick={handleSubmit} color="primary">
                Save
              </InnerButton>
            )}
            
            {(hasSubmitTc) && (
              <NavLink to={`/v4/tc/${formtype}/certified-standard-weight/${TE_ID}`}>
                <InnerButton>Back</InnerButton>
              </NavLink>
            )}

            {completionMap["declaration-submit-details"] && (
              <Tooltip title={"Click to Submit"}>
                <span>
                  <InnerButton onClick={handleTCSubmit}>Submit</InnerButton>
                </span>
              </Tooltip>
            )}

            {completionMap["declaration-submit-details"] && (
              <Tooltip title={"Click to Preview"}>
                <span>
                  <InnerButton onClick={togglePreviewDrawer}>Preview</InnerButton>
                </span>
              </Tooltip>
            )}

            {completionMap["declaration-submit-details"] && (isSubmitted || showDownload) && (
              <Tooltip title={"Click to Download"}>
                <span>
                  <InnerButton 
                    onClick={handleDownload}
                    disabled={isDownloading}
                    sx={{
                      position: "relative",
                      "& .loading-indicator": {
                        position: "absolute",
                        left: "50%",
                        transform: "translateX(-50%)",
                      },
                    }}
                  >
                    {isDownloading ? (
                      <Box className="loading-indicator" display="flex" alignItems="center">
                        <CircularProgress size={20} color="inherit" />
                      </Box>
                    ) : (
                      "Download Word"
                    )}
                  </InnerButton>
                </span>
              </Tooltip>
            )}
          </Box>

          {TE_ID && (
            <ViewerModalPopup open={previewOpen} onClose={togglePreviewDrawer} title="Preview">
              <div style={{ width: "100%", height: "100vh" }}>
                <PdfViewer apiUrl={TCAppConfig.PREVIEW_TC_V4} params={{ tcId: TE_ID }} />
              </div>
            </ViewerModalPopup>
          )}
        </>
      )}
    </Box>
  );
};

export default DeclarationByCertificateBody;
